import React, { useState, useEffect, useCallback } from 'react';
import { Table, AutoComplete, Input, Button, message, Tag, Modal, Form, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Api_VariavelGlobal } from '../global';

const { Option } = Select;

const Users_List = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [userSelected, setUserSelected] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();
  const [showInativos, setShowInativos] = useState(false);
  const [tipoUsuarioFilter, setTipoUsuarioFilter] = useState('all');
  const [tiposUsuario, setTiposUsuario] = useState([]);
  const navigate = useNavigate();

  // Função utilitária
  const formatarData = (dataISO) => {
    if (!dataISO) return "";
    const cleaned = dataISO.replace("Z", "");
    const [data, hora] = cleaned.split("T");
    if (!data || !hora) return dataISO;
    const [ano, mes, dia] = data.split("-");
    const horaLimpa = hora.split(".")[0];
    return `${dia}/${mes}/${ano} ${horaLimpa}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "xx/xx/xxxx";
    if (dateStr.includes('/')) return dateStr;
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return "xx/xx/xxxx";
    return `${day.padStart(2,'0')}/${month.padStart(2,'0')}/${year}`;
  };

  // 🔹 Buscar usuários
  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${Api_VariavelGlobal}/api/users`);
      if (!response.ok) throw new Error('Erro ao buscar usuários');
      const users = await response.json();

      const usersFormatted = users.map(user => ({
        key: user.ID_USUARIO,
        name: user.LOGIN,
        email: user.EMAIL,
        telefone: user.TELEFONE || '-',
        nascimento: formatDate(user.NASCIMENTO),
        tipoUsuarioId: String(user.tipoUsuario?.ID_TIPO_USUARIO || '0'),
        tipoUsuario: user.tipoUsuario?.DESCRICAO || 'Desconhecido',
        status: user.STATUS,
        data: formatarData(user.DATA),
      }));

      setData(usersFormatted);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      message.error('Erro ao buscar usuários.');
    }
  }, []);

  // 🔹 Buscar tipos de usuário
  const fetchTiposUsuario = useCallback(async () => {
    try {
      const response = await fetch(`${Api_VariavelGlobal}/api/tipoUsuarios`);
      if (!response.ok) throw new Error('Erro ao buscar tipos de usuário');
      const tipos = await response.json();
      setTiposUsuario(tipos);
    } catch (error) {
      console.error('Erro ao buscar tipos de usuário:', error);
      message.error('Erro ao buscar tipos de usuário.');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchTiposUsuario();
  }, [fetchUsers, fetchTiposUsuario]);

  // 🔹 Aplicar filtros
  useEffect(() => {
    const filtered = data.filter(user => {
      const matchesStatus = showInativos || user.status === 1;
      const matchesTipoUsuario = tipoUsuarioFilter === 'all' || user.tipoUsuarioId === tipoUsuarioFilter;
      const searchLower = searchValue.toLowerCase();
      const matchesSearch =
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.telefone.toLowerCase().includes(searchLower) ||
        user.tipoUsuario.toLowerCase().includes(searchLower);

      return matchesStatus && matchesTipoUsuario && matchesSearch;
    });

    setFilteredData(filtered);
    setOptions(
      filtered.map(user => ({
        value: user.name,
        label: `${user.name} - ${user.email}`,
      }))
    );
  }, [data, searchValue, showInativos, tipoUsuarioFilter]);

  const handleSearch = (value) => setSearchValue(value);
  const onSelect = (value) => setSearchValue(value);

  const showEditModal = (user) => {
    setUserSelected(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      telefone: user.telefone,
      nascimento: user.nascimento,
      tipoUsuarioId: user.tipoUsuarioId,
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);

      const response = await fetch(`${Api_VariavelGlobal}/api/user/${userSelected.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          LOGIN: values.name,
          NASCIMENTO: values.nascimento,
          EMAIL: values.email,
          TELEFONE: values.telefone,
          STATUS: userSelected.status,
          ID_TIPO_USUARIO: values.tipoUsuarioId,
        }),
      });

      if (response.ok) {
        message.success('Usuário atualizado com sucesso!');
        setIsModalVisible(false);
        fetchUsers();
      } else {
        message.error('Erro ao atualizar usuário.');
      }
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      message.error('Erro ao atualizar usuário.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus = userSelected.status === 1 ? 0 : 1;
      const response = await fetch(`${Api_VariavelGlobal}/api/user/${userSelected.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ STATUS: newStatus }),
      });

      if (response.ok) {
        message.success(`Usuário ${newStatus === 1 ? 'ativado' : 'desativado'} com sucesso.`);
        setIsModalVisible(false);
        fetchUsers();
      } else {
        message.error('Erro ao atualizar status do usuário.');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      message.error('Erro ao atualizar status.');
    }
  };

  const handleDeleteUser = () => {
    Modal.confirm({
      title: 'Excluir definitivamente?',
      content: 'Essa ação removerá permanentemente o usuário da base de dados.',
      okText: 'Sim, excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const response = await fetch(`${Api_VariavelGlobal}/api/user/${userSelected.key}`, {
            method: 'DELETE',
          });

          if (response.ok) {
            message.success('Usuário excluído definitivamente.');
            setIsModalVisible(false);
            fetchUsers();
          } else {
            message.error('Erro ao excluir usuário.');
          }
        } catch (error) {
          console.error('Erro ao excluir usuário:', error);
          message.error('Erro ao excluir usuário.');
        }
      },
    });
  };

  const columns = [
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Telefone', dataIndex: 'telefone', key: 'telefone' },
    { title: 'Nascimento', dataIndex: 'nascimento', key: 'nascimento', width: 120 },
    {
      title: 'WhatsApp',
      key: 'whatsapp',
      render: (_, user) => {
        const phone = user.telefone.replace(/\D/g, '');
        return (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              if (!phone) return;
              const whatsappWindowName = 'whatsappTab';
              const text = encodeURIComponent('Olá, aqui é do Bebilogo, podemos falar?');
              window.open(`https://wa.me/55${phone}?text=${text}`, whatsappWindowName);
            }}
            disabled={!phone}
          >
            Conversar
          </Button>
        );
      },
    },
    { title: 'Tipo de Usuário', dataIndex: 'tipoUsuario', key: 'tipoUsuario' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => status === 1 ? <Tag color="blue">Ativo</Tag> : <Tag color="red">Inativo</Tag>,
    },
    { title: 'Data', dataIndex: 'data', key: 'data', width: 120 },
    {
      title: 'Ação',
      key: 'action',
      render: (_, user) => (
        <Button type="link" onClick={() => showEditModal(user)}>
          Editar
        </Button>
      ),
    },
  ];

  return (
    <div style={{ background: '#fff', minHeight: '100vh', padding: 16 }}>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Select
          value={tipoUsuarioFilter}
          onChange={(value) => setTipoUsuarioFilter(value)}
          style={{ width: 200 }}
        >
          <Option value="all">Todos os Tipos</Option>
          {tiposUsuario.map((tipo) => (
            <Option key={tipo.ID_TIPO_USUARIO} value={String(tipo.ID_TIPO_USUARIO)}>
              {tipo.DESCRICAO}
            </Option>
          ))}
        </Select>

        <AutoComplete
          style={{ width: 300 }}
          value={searchValue}
          options={options}
          onSelect={onSelect}
          onSearch={handleSearch}
          placeholder="Buscar usuários"
          allowClear
        >
          <Input.Search size="middle" />
        </AutoComplete>

        <Button type="primary" onClick={() => navigate('/User_Form')}>
          Cadastrar Usuário
        </Button>
        <Button onClick={() => setShowInativos(prev => !prev)}>
          {showInativos ? 'Ocultar Inativos' : 'Mostrar Inativos'}
        </Button>
      </div>

      <Table columns={columns} dataSource={filteredData} rowKey="key" size="small" />

      {/* Modal de edição */}
      <Modal
        title="Editar Usuário"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={confirmLoading}
        footer={[
          userSelected?.tipoUsuarioId !== '1' && (
            <Button key="toggle" onClick={handleToggleStatus}>
              {userSelected?.status === 1 ? 'Desativar' : 'Ativar'}
            </Button>
          ),
          userSelected?.tipoUsuarioId !== '1' && (
            <Button key="delete" danger onClick={handleDeleteUser}>
              Excluir
            </Button>
          ),
          <Button key="cancel" onClick={handleModalCancel}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" loading={confirmLoading} onClick={handleModalOk}>
            Salvar
          </Button>,
        ].filter(Boolean)}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Nome" name="name" rules={[{ required: true, message: 'Informe o nome' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Nascimento" name="nascimento" rules={[{ required: true, message: 'Informe a data' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Tipo de Usuário" name="tipoUsuarioId" rules={[{ required: true, message: 'Selecione o tipo' }]}>
            <Select placeholder="Selecione o tipo">
              {tiposUsuario.map((tipo) => (
                <Option key={tipo.ID_TIPO_USUARIO} value={String(tipo.ID_TIPO_USUARIO)}>
                  {tipo.DESCRICAO}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Informe o email' },
              { type: 'email', message: 'Email inválido' },
            ]}
          >
            <Input disabled />
          </Form.Item>

          <Form.Item label="Telefone" name="telefone">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users_List;
